"use client";

import { useMemo, useState } from "react";

type RatingStarsProps = {
  value: number;
  onChange?: (value: number) => void;
  size?: "sm" | "md";
  readOnly?: boolean;
};

const starSizes = {
  sm: "h-4 w-4",
  md: "h-6 w-6",
};

export default function RatingStars({ value, onChange, size = "sm", readOnly }: RatingStarsProps) {
  const [hoverValue, setHoverValue] = useState<number | null>(null);
  const isInteractive = typeof onChange === "function" && !readOnly;

  const displayValue = useMemo(() => {
    if (isInteractive && hoverValue !== null) return hoverValue;
    return value;
  }, [hoverValue, isInteractive, value]);

  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, index) => {
        const starValue = index + 1;
        const filled = displayValue >= starValue;
        const classes = filled ? "text-green-600" : "text-gray-300";
        const sharedClass = `${starSizes[size]} transition-colors`;
        if (isInteractive) {
          return (
            <button
              key={starValue}
              type="button"
              onMouseEnter={() => setHoverValue(starValue)}
              onMouseLeave={() => setHoverValue(null)}
              onClick={() => onChange?.(starValue)}
              className="rounded focus:outline-none focus:ring-2 focus:ring-green-200"
              aria-label={`Calificar ${starValue} estrellas`}
            >
              <StarIcon className={`${sharedClass} ${classes}`} />
            </button>
          );
        }
        return <StarIcon key={starValue} className={`${sharedClass} ${classes}`} />;
      })}
    </div>
  );
}

function StarIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M10 15.27l-5.18 3.73 1.98-6.04-5.02-3.64h6.21L10 3l2.01 6.32h6.21l-5.02 3.64 1.98 6.04z" />
    </svg>
  );
}
